import { inject, Injectable } from '@angular/core';
import {
   collection,
   collectionData,
   CollectionReference,
   deleteDoc,
   doc,
   Firestore,
   getDoc,
   getDocs,
   serverTimestamp,
   setDoc,
   Timestamp,
   updateDoc,
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { Package, PackageDraft } from '../models/package.model';
import { UserService } from '../user/user.service';
import { StoreService } from '../store/store.service';
import { Store } from '../store/store';

@Injectable({ providedIn: 'root' })
export class PackageService {
   private firestore = inject(Firestore);
   private userService = inject(UserService);
   private storeService = inject(StoreService);

   private packagesRef = collection(
      this.firestore,
      'packages',
   ) as CollectionReference<Package>;

   /** Real-time observable of all packages, sorted by sortOrder */
   packages$: Observable<Package[]> = collectionData(this.packagesRef, {
      idField: 'id',
   });

   /** Cache for synchronous lookups (populated from the realtime feed) */
   private cache = new Map<string, Package>();

   constructor() {
      this.packages$.subscribe({
         next: (pkgs) => {
            this.cache.clear();
            for (const p of pkgs) this.cache.set(p.id, p);
         },
         // Keep quiet on permission errors (e.g. rules not yet deployed) —
         // an empty cache falls back to legacy global-flag behaviour.
         error: (e) => console.warn('Packages feed unavailable:', e?.code || e),
      });
   }

   /** All packages (one-shot) sorted by sortOrder */
   async getAll(): Promise<Package[]> {
      const snap = await getDocs(this.packagesRef);
      return snap.docs
         .map((d) => d.data())
         .sort((a, b) => a.sortOrder - b.sortOrder);
   }

   /** Single package by id (one-shot) */
   async getById(id: string): Promise<Package | null> {
      const snap = await getDoc(doc(this.packagesRef, id));
      return snap.exists() ? snap.data() : null;
   }

   /** Synchronous lookup from the realtime cache */
   getByIdSync(id: string | null | undefined): Package | null {
      if (!id) return null;
      return this.cache.get(id) ?? null;
   }

   /**
    * The package assigned to a store, or null when none is assigned. Reads the
    * store's `packageId` from the live current store, else the store doc, then
    * loads the package (cache first, then a fetch) — so it's correct even on a
    * cold navigation before the realtime caches are warm.
    */
   async getForStore(storeId: string): Promise<Package | null> {
      let packageId: string | undefined;
      const current = this.userService.currentStore$.value;
      if (current && current.id === storeId) {
         packageId = current.packageId;
      } else {
         try {
            const snap = await getDoc(doc(this.storeService.storeRef, storeId));
            packageId = snap.exists()
               ? (snap.data() as Store).packageId
               : undefined;
         } catch {
            packageId = undefined;
         }
      }
      if (!packageId) return null;
      return this.getByIdSync(packageId) ?? (await this.getById(packageId));
   }

   /** Create a new package, returns the generated id */
   async create(draft: PackageDraft): Promise<string> {
      const ref = doc(this.packagesRef);
      const pkg: Package = {
         ...draft,
         id: ref.id,
         createdAt: serverTimestamp() as unknown as Timestamp,
         updatedAt: serverTimestamp() as unknown as Timestamp,
         updatedBy: this.actor(),
      };
      await setDoc(ref, pkg);
      return ref.id;
   }

   /** Update an existing package */
   async update(id: string, draft: PackageDraft): Promise<void> {
      await updateDoc(doc(this.packagesRef, id), {
         ...draft,
         updatedAt: serverTimestamp() as unknown as Timestamp,
         updatedBy: this.actor(),
      });
   }

   /** Activate / deactivate a package without touching its other fields. */
   async setActive(id: string, isActive: boolean): Promise<void> {
      await updateDoc(doc(this.packagesRef, id), {
         isActive,
         updatedAt: serverTimestamp() as unknown as Timestamp,
         updatedBy: this.actor(),
      });
   }

   /** Delete a package permanently */
   async remove(id: string): Promise<void> {
      await deleteDoc(doc(this.packagesRef, id));
   }

   private actor(): { uid: string; displayName: string } {
      const u = this.userService.currentUser$.value;
      return { uid: u?.id || '', displayName: u?.displayName || 'System' };
   }
}
